/** Employee API shapes and small formatting helpers. */
import type { EmployeeProfileData } from "./types.js";
export const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
export function minutesLabel(value: number): string {
  const sign = value < 0 ? "−" : "";
  const absolute = Math.abs(value);
  return `${sign}${Math.floor(absolute / 60)}h ${String(absolute % 60).padStart(2, "0")}m`;
}
export function maskIban(value: string | null | undefined): string {
  if (!value) return "";
  const compact = value.replace(/\s/g, "");
  return compact.length > 4
    ? `${"•".repeat(Math.max(0, compact.length - 4))}${compact.slice(-4)}`
    : compact;
}
export type RawEmployee = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  locale: string;
  status: EmployeeProfileData["user"]["status"];
  roles: EmployeeProfileData["user"]["roles"];
  profile: null | {
    employeeNumber: string | null;
    phone: string | null;
    addressLine1: string | null;
    addressLine2: string | null;
    postalCode: string | null;
    city: string | null;
    country: string;
    dateOfBirth: string | null;
    ahvNumber: string | null;
    bankName: string | null;
    bankIban: string | null;
    nationality: string | null;
    jobTitle: string | null;
    employmentStart: string | null;
    employmentEnd: string | null;
    pensumPercent: string;
    salaryType: "monthly" | "hourly" | null;
    salaryAmount: string | null;
    salaryCurrency: string;
    salaryBasis: "full_time" | "actual_pensum";
    laborCostRate?: string | null;
    hasThirteenthSalary: boolean;
    vacationWeeksOverride: string | null;
    internalNotes: string | null;
  };
  schedule: EmployeeProfileData["schedule"];
  balances: Array<{
    year: number;
    vacationGrantedMinutes: number;
    vacationAdjustmentMinutes: number;
    compensationAdjustmentMinutes: number;
  }>;
};
export function normalizeEmployee(raw: RawEmployee): EmployeeProfileData {
  return {
    user: {
      id: raw.id,
      email: raw.email,
      firstName: raw.firstName,
      lastName: raw.lastName,
      locale: raw.locale,
      status: raw.status,
      roles: raw.roles,
    },
    profile: raw.profile
      ? {
          ...raw.profile,
          employmentStartDate: raw.profile.employmentStart,
          employmentEndDate: raw.profile.employmentEnd,
          notes: raw.profile.internalNotes,
        }
      : null,
    schedule: raw.schedule,
    balances: raw.balances.map((balance) => ({
      ...balance,
      vacationUsedMinutes: 0,
      vacationRemainingMinutes:
        balance.vacationGrantedMinutes + balance.vacationAdjustmentMinutes,
      compensationEarnedMinutes: 0,
      compensationRemainingMinutes: balance.compensationAdjustmentMinutes,
    })),
  };
}
